import { motion } from "framer-motion";
import { ArrowUpRight, Globe2, Mic } from "lucide-react";
import { LANGUAGES } from "@/data";
import { useApply } from "@/context";

const reveal = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.07 },
  }),
};

function LanguageCard({ lang, index }) {
  const { openApply } = useApply();
  return (
    <motion.article
      custom={index}
      variants={reveal}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      id={lang.id}
      data-testid={`language-card-${lang.id}`}
      className="group relative flex scroll-mt-28 flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-7 backdrop-blur transition-all duration-500 hover:-translate-y-2 hover:border-vibrant-orange/40 hover:bg-white/[0.08]"
    >
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${lang.accent} opacity-70`} />

      <div className="flex items-start justify-between">
        <span className="text-5xl leading-none" aria-hidden="true">{lang.flag}</span>
        <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white/70">
          {lang.level}
        </span>
      </div>

      <h3 className="mt-6 font-heading text-3xl font-extrabold tracking-tight text-white">
        {lang.name}
      </h3>
      <p className="mt-1 font-heading text-lg text-emerald-green">{lang.native}</p>
      <p className="mt-4 flex-1 text-sm leading-relaxed text-white/60">{lang.tagline}</p>

      <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5">
        <span className="inline-flex items-center gap-2 text-xs text-white/50">
          <Mic size={13} className="text-emerald-green" /> {lang.weeks} · voice-first
        </span>
        <button
          data-testid={`language-apply-${lang.id}`}
          onClick={() => openApply(`${lang.name} (Language)`)}
          className="inline-flex items-center gap-1 text-sm font-semibold text-vibrant-orange transition-colors hover:text-orange-300"
        >
          Enroll
          <ArrowUpRight size={14} />
        </button>
      </div>
    </motion.article>
  );
}

export default function Languages() {
  return (
    <section
      id="languages"
      data-testid="languages-section"
      className="relative overflow-hidden bg-deep-trust-blue py-24 text-white md:py-32"
    >
      <div className="pointer-events-none absolute -right-40 top-10 h-96 w-96 rounded-full bg-vibrant-orange/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-emerald-green/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-14 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
              <Globe2 size={16} /> Global Languages
            </span>
            <h2 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              Speak the world&apos;s
              <span className="block text-vibrant-orange">career languages.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/60 md:text-lg">
              Voice-first fluency programs built for Indian learners going into nursing, hospitality, IT and
              global trade. Every course is spoken, listened and role-played — not just written.
            </p>
          </div>
          <p className="rounded-full border border-white/10 bg-white/5 px-5 py-2 text-xs text-white/60">
            6 languages · CEFR-aligned · placement support
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {LANGUAGES.map((lang, i) => (
            <LanguageCard key={lang.id} lang={lang} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
